import { useNavigate } from "react-router-dom";
import Aside from "../../components/Aside";
import CustomDataTable from "../../components/CustomDataTable";
import Footer from "../../components/Footer";
import Header from "../../components/Header";
import { CampaignsMonitoringService } from "../../services/CampaignsMonitoringService";
import { useEffect, useRef, useState } from "react";
import { useCurrentAccount } from "../../hooks/useCurrentAccount";
import PieChart from "../../components/PieChart";
import Button from "../../components/form-controls/Button";
import { AlertService } from "../../services/Alert";
import { useTranslation } from "react-i18next";
import useRequest from "../../hooks/useRequest";
import { ChannelUtil } from "../../utils/channel";
import { useSession } from "../../hooks/useSession";
import ModalMessagePreview from "./ModalMessagePreview";

function CampaignsMonitoring() {
  const { t } = useTranslation();
  const { account } = useCurrentAccount();
  const { call, isRequesting } = useRequest();
  const { session } = useSession();
  const [lastUpdate, setLastUpdate] = useState("yyyy-mm-dd 00:00:00");
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState<number | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<string | null>(null);

  const handlePreviewMessage = (message: string) => {
    setSelectedMessage(message);
    setShowPreviewModal(true);
  };

  const handleClosePreviewModal = () => {
    setShowPreviewModal(false);
    setSelectedMessage(null);
  };

  const columns = [
    {
      name: t("bulk-senders.name"),
      sortable: true,
      center: true,
      cell: (row: any) => {
        return (
          <h4
            className="mb-0 text-nowrap text-truncate"
            style={{
              maxWidth: "9rem",
            }}
          >
            {row.info.base_name}
          </h4>
        );
      },
    },
    {
      name: t("bulk-senders.app"),
      sortable: false,
      center: true,
      cell: (row) => {
        return (
          <div>
            {(() => {
              switch (ChannelUtil.format(row.info.app)) {
                case "WhatsApp Business":
                  return (
                    <div>
                      <span className="badge bg-soft-primary text-primary">
                        {" "}
                        <i className="bi bi-whatsapp"></i>{" "}
                        {t("platform.channels.links-page.wa-business")}{" "}
                      </span>
                    </div>
                  );
                case "RCS":
                  return (
                    <div>
                      <span className="badge badge-soft-rcs">
                        {" "}
                        <i className="bi bi-chat-left-text"></i>{" "}
                        {t("platform.channels.links-page.rcs")}{" "}
                      </span>
                    </div>
                  );
                case "SMS":
                  return (
                    <div>
                      <span className="badge bg-soft-info text-info">
                        {" "}
                        <i className="bi bi-chat-text"></i>{" "}
                        {t("platform.channels.links-page.sms")}{" "}
                      </span>
                    </div>
                  );
                default:
                  return (
                    <div>
                      <span className="badge bg-soft-danger text-danger">
                        {t("platform.channels.links-page.badge-alert")}{" "}
                      </span>
                    </div>
                  );
              }
            })()}
          </div>
        );
      },
    },

    {
      name: t("bulk-senders.statistics"),
      sortable: false,
      center: true,
      cell: (row: any) => {
        let resume = {
          total_read: {
            label: t("bulk-senders.read"),
            total: 0,
            color: "#FAA916",
          },
          total_delivered: {
            label: t("bulk-senders.delivered"),
            total: 0,
            color: "#579743",
          },
          total_sent: {
            label: t("bulk-senders.sent"),
            total: 0,
            color: "#0070BC",
          },
          total_no_sent: {
            label: t("bulk-senders.no-sent"),
            total: 0,
            color: "#575A5C",
          },
          total_failed: {
            label: t("bulk-senders.failed"),
            total: 0,
            color: "#CE4532",
          },
          total_convertion: {
            label: t("bulk-senders.convertion"),
            total: 0,
            color: "#13A1AB",
          },
        };

        const keys = Object.keys(row.chart);
        keys.forEach((k) => {
          if (resume[k]) {
            resume[k].total = row.chart[k];
          }
        });
        return (
          <div>
            <PieChart
              datasetName="Resumen"
              dataset={{
                labels: [
                  resume.total_read.label,
                  resume.total_delivered.label,
                  resume.total_sent.label,
                  resume.total_no_sent.label,
                  resume.total_failed.label,
                  resume.total_convertion.label,
                ],
                data: [
                  resume.total_read.total,
                  resume.total_delivered.total,
                  resume.total_sent.total,
                  resume.total_no_sent.total,
                  resume.total_failed.total,
                  resume.total_convertion.total,
                ],
                backgroundColor: [
                  resume.total_read.color,
                  resume.total_delivered.color,
                  resume.total_sent.color,
                  resume.total_no_sent.color,
                  resume.total_failed.color,
                  resume.total_convertion.color,
                ],
              }}
            />
          </div>
        );
      },
    },
    {
      name: t("bulk-senders.data-sent"),
      sortable: false,
      center: false,
      cell: (row: any) => {
        const getStatusDetails = (status) => {
          switch (status) {
            case "ACTIVA":
              return {
                className: "badge bg-soft-primary text-primary fs-6",
                icon: <i className="bi bi-check-circle-fill fs-5"></i>,
                statusText: t("bulk-senders.data-status-active"),
              };
            case "LIMPIANDO":
              return {
                className: "badge bg-soft-info text-info fs-6",
                icon: <i className="bi bi-brush fs-5"></i>,
                statusText: t("bulk-senders.data-status-clean"),
              };
            case "ENVIANDO":
              return {
                className: "badge bg-soft-success text-success fs-6",
                icon: <i className="bi bi-send fs-5"></i>,
                statusText: t("bulk-senders.data-status-sending"),
              };
            case "DESACTIVA":
              return {
                className: "badge bg-soft-danger text-danger fs-6",
                icon: <i className="bi bi-dash-circle-fill fs-5"></i>,
                statusText: t("bulk-senders.data-status-inactive"),
              };
            default:
              return {
                className: "badge bg-soft-warning text-warning fs-6",
                icon: <i className="bi bi-bookmark-fill fs-5"></i>,
                statusText: t("bulk-senders.data-no-info"),
              };
          }
        };

        const statusDetails = getStatusDetails(row.status.process_status);

        return (
          <>
            <p className="mb-0 text-nowrap">
              <span className="h5">{t("bulk-senders.data-status")}: </span>
              <span className={statusDetails.className}>
                {statusDetails.icon} {statusDetails.statusText}
              </span>
            </p>
            <p className="mb-0 text-nowrap">
              <span className="h5">{t("bulk-senders.data-name-sub")}: </span>{" "}
              {row.info.sub_account_name || t("bulk-senders.data-no-info")}
            </p>
            <p className="mb-0 text-nowrap">
              <span className="h5">{t("bulk-senders.data-first-send")}: </span>{" "}
              {row.status.first_sent || t("bulk-senders.data-no-info")}
            </p>
            <p className="mb-0 text-nowrap">
              <span className="h5">{t("bulk-senders.data-last-send")}: </span>{" "}
              {row.status.last_sent || t("bulk-senders.data-no-info")}
            </p>
            <p className="mb-0 text-nowrap">
              <span className="h5">{t("bulk-senders.data-duration")}: </span>{" "}
              {row.status.duration || t("bulk-senders.data-no-info")}
            </p>
          </>
        );
      },
    },
    {
      name: t("campaigns.monitoring.user-sender"),
      sortable: false,
      center: true,
      cell: (row: any) => {
        return (
          <h4
            className="mb-0 text-nowrap text-truncate"
            style={{
              margin: "0 clamp(15px, 2vw, 50px)",
              maxWidth: "9rem",
            }}
          >
            {row.info.who_send ? (
              row.info.who_send
            ) : (
              <>
                <i
                  className="bi bi-exclamation-circle text-muted"
                  title={t("campaigns.monitoring.no-data")}
                ></i>{" "}
                {t("campaigns.monitoring.no-data")}
              </>
            )}
          </h4>
        );
      },
    },
    {
      name: t("campaigns.monitoring.message-template"),
      sortable: false,
      center: true,
      cell: (row: any) => {
        return (
          <Button
            className="btn btn-primary btn-sm"
            onClick={() => handlePreviewMessage(row.message_send)}
          >
            <i className="bi-eye-fill me-1"></i>
            {t("campaigns.monitoring.preview-message")}
          </Button>
        );
      },
    },
    {
      name: t("bulk-senders.actions"),
      sortable: false,
      center: true,
      cell: (row: any) => {
        return (
          <div className="btn-group" role="group">
            <Button
              className="btn btn-danger btn-sm"
              onClick={() => cancelCampaign(row.info.base_id)}
            >
              <i className="bi-x-circle"></i>{" "}
              {t("campaigns.monitoring.cancel-campaign")}
            </Button>
          </div>
        );
      },
    },
  ];

  const getActiveSends = async () => {
    const accountId = session.profile === "Root" ? selectedAccount : account.id;

    try {
      const { data } = await call(() =>
        CampaignsMonitoringService.getActiveSends(accountId)
      );
      setData(data);
    } catch (error) {
      console.error(t("bulk-senders.error-get-active-campaigns"), error);
      AlertService.toastError(t("bulk-senders.error-get-active-campaigns"));
    }
  };

  const cancelCampaign = async (campaignId: number) => {
    try {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      await AlertService.warning(
        t("campaigns.monitoring.modal-title-cancel-campaign"),
        t("campaigns.monitoring.modal-content-cancel-campaign"),
        async () => {
          await call(() =>
            CampaignsMonitoringService.abortCampaign(account.id, campaignId)
          );
          AlertService.toastSuccess(
            t("campaigns.monitoring.toast-success-cancel-campaign")
          );
          getMonitoring();
        }
      );
    } catch (error) {
      console.error(error);
      AlertService.toastError(t("campaigns.monitoring.error-cancel-campaign"));
    } finally {
      timeoutRef.current = setTimeout(getMonitoring, 15000);
    }
  };

  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const getMonitoring = async () => {
    try {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      await getActiveSends();
      setLastUpdate(formatDate(new Date()));

      AlertService.toastSuccess(t("dashboard-operations.update-text"));
    } catch (error) {
      console.error(t("dashboard-operations.error-5"), error);
      AlertService.toastError(t("dashboard-operations.error-5"));
    } finally {
      timeoutRef.current = setTimeout(getMonitoring, 30000);
    }
  };

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    getMonitoring();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [selectedAccount]);

  return (
    <>
      <Header />
      <Aside />

      <main id="content" role="main" className="main">
        <div className="content container-fluid">
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col-sm mb-2 mb-sm-0">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb breadcrumb-no-gutter">
                    <li className="breadcrumb-item ">
                      <span className="breadcrumb-link">
                        {t("campaigns.monitoring.breadcrumb-campaigns")}
                      </span>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      {t("campaigns.monitoring.breadcrumb-follow-up")}
                    </li>
                  </ol>
                </nav>
                <h1 className="page-header-title">
                  <i className="bi-send-plus nav-icon"></i>{" "}
                  {t("campaigns.monitoring.header-title")}
                </h1>
                <div className="col-sm-auto d-flex align-items-center">
                  <i
                    className="bi-arrow-repeat nav-icon me-2 text-primary cursor-pointer display-4"
                    onClick={() => {
                      getMonitoring();
                      clearInterval(timeoutRef.current);
                    }}
                  ></i>

                  <span className="badge bg-soft-primary text-primary rounded-pill fs-4">
                    {t("dashboard-operations.last-update")}: {lastUpdate}{" "}
                  </span>
                </div>
              </div>
              <div className="col-sm-auto">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => navigate("/campaigns/create")}
                >
                  <i className="bi-chat-dots-fill me-1"></i>
                  {t("bulk-senders.add-campaign")}{" "}
                </button>
              </div>
            </div>
          </div>
          <CustomDataTable
            columns={columns}
            data={data}
            isLoading={isRequesting}
            onAccountsSelected={(accounts) => {
              if (accounts && accounts.length > 0) {
                setSelectedAccount(accounts[0].value);
              } else {
                setSelectedAccount(null);
              }
            }}
            accountsDefaultValue={
              session.profile !== "Root"
                ? { value: account.id, label: account.nombre }
                : undefined
            }
            // hideAccountsFilter={session.profile !== "Root"}
            hideAccountsFilter
            filter={(row, term) => {
              if (!term) return true;
              const regex = new RegExp(term, "i");
              return (
                row.info?.who_send?.match(regex) ||
                row.info?.base_name?.match(regex) ||
                row.info?.app?.match(regex)
              );
            }}
          />
        </div>
        <Footer />
      </main>
      <ModalMessagePreview
        message={selectedMessage}
        show={showPreviewModal}
        onRequestClose={handleClosePreviewModal}
      />
    </>
  );
}
export default CampaignsMonitoring;
